const MIN_ROWS = 1;
const MIN_COLS = 1;
const MAX_ROWS = 20;
const MAX_COLS = 10;

export function createEmptyGrid(rows, cols) {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
}

export function createCustomTable({
  rows = 3,
  cols = 3,
  title = 'Custom table',
  headerRow = true,
} = {}) {
  const safeRows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, rows));
  const safeCols = Math.min(MAX_COLS, Math.max(MIN_COLS, cols));
  const cells = createEmptyGrid(safeRows, safeCols);
  if (headerRow && safeRows > 0) {
    for (let c = 0; c < safeCols; c += 1) {
      cells[0][c] = `Column ${c + 1}`;
    }
  }
  return {
    id: `table_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    title,
    headerRow,
    rows: safeRows,
    cols: safeCols,
    cells,
    layout: { x: 0, y: 0, w: 0, h: 0 },
  };
}

export function normalizeCustomTable(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const rows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, Number(raw.rows) || 1));
  const cols = Math.min(MAX_COLS, Math.max(MIN_COLS, Number(raw.cols) || 1));
  const cells = createEmptyGrid(rows, cols);
  (raw.cells || []).forEach((row, r) => {
    if (r >= rows) return;
    (row || []).forEach((cell, c) => {
      if (c < cols) cells[r][c] = cell == null ? '' : String(cell);
    });
  });
  return {
    id: raw.id || `table_${Date.now()}`,
    title: raw.title == null ? '' : String(raw.title),
    headerRow: raw.headerRow !== false,
    rows,
    cols,
    cells,
    layout: {
      x: Number(raw.layout?.x) || 0,
      y: Number(raw.layout?.y) || 0,
      w: Number(raw.layout?.w) || 0,
      h: Number(raw.layout?.h) || 0,
    },
  };
}

export function normalizeCustomTables(tables) {
  if (!Array.isArray(tables)) return [];
  return tables.map(normalizeCustomTable).filter(Boolean);
}

export function resizeCustomTable(table, nextRows, nextCols) {
  const rows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, nextRows));
  const cols = Math.min(MAX_COLS, Math.max(MIN_COLS, nextCols));
  const cells = createEmptyGrid(rows, cols);
  table.cells.forEach((row, r) => {
    if (r >= rows) return;
    row.forEach((cell, c) => {
      if (c < cols) cells[r][c] = cell;
    });
  });
  return { ...table, rows, cols, cells };
}

export function addCustomTableRow(table) {
  if (table.rows >= MAX_ROWS) return table;
  const cells = [...table.cells, Array.from({ length: table.cols }, () => '')];
  return { ...table, rows: table.rows + 1, cells };
}

export function removeCustomTableRow(table, rowIndex) {
  if (table.rows <= MIN_ROWS) return table;
  const cells = table.cells.filter((_, i) => i !== rowIndex);
  return { ...table, rows: cells.length, cells };
}

export function addCustomTableCol(table) {
  if (table.cols >= MAX_COLS) return table;
  const cells = table.cells.map((row) => [...row, '']);
  return { ...table, cols: table.cols + 1, cells };
}

export function removeCustomTableCol(table, colIndex) {
  if (table.cols <= MIN_COLS) return table;
  const cells = table.cells.map((row) => row.filter((_, i) => i !== colIndex));
  return { ...table, cols: cells[0]?.length || 1, cells };
}

export function updateCustomTableCell(table, rowIndex, colIndex, value) {
  const cells = table.cells.map((row, r) => {
    if (r !== rowIndex) return [...row];
    return row.map((cell, c) => (c === colIndex ? value : cell));
  });
  return { ...table, cells };
}

export function tableHasContent(table) {
  return (table.cells || []).some((row) => row.some((cell) => String(cell || '').trim()));
}

export { MIN_ROWS, MIN_COLS, MAX_ROWS, MAX_COLS };
